import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, Users, Presentation, Mic2, HeartHandshake, Check, ChevronDown, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DynamicForm from "@/components/forms/DynamicForm";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    id: "tutoring",
    icon: BookOpen,
    title: "Tutoring",
    tag: "Grades K–12",
    body: "Identify your child's strengths and areas for improvement in Reading and Math — personalized care to help them excel and become fluent.",
    points: ["Reading & Math subject focus", "Pre & post assessments", "Regular monitoring", "1–2 teacher connections"],
  },
  {
    id: "mentorship",
    icon: Users,
    title: "Mentorship",
    tag: "Youth 10–18 · Educators",
    body: "A professional working relationship that partners and connects over time to support the personal and professional growth of every mentee.",
    points: ["One-on-one & group sessions", "Career development planning", "Weekly check-ins", "Self evaluations & listening sessions"],
  },
  {
    id: "workshops",
    icon: Presentation,
    title: "Workshops & Seminars",
    tag: "Teachers · Administrators · Community",
    body: "Hands-on workshops and information-based seminars — leadership development, classroom management, school turnaround, data, climate & culture.",
    points: ["Leadership development", "Diversity training", "Team building & PLCs", "Virtual webinars available"],
  },
  {
    id: "speaking",
    icon: Mic2,
    title: "Public Speaking",
    tag: "Live & Virtual",
    body: "Captivate your audience — advocating, informing, and persuading attendees to take action, change atmospheres, and grow through a purposeful message.",
    points: ["Keynote & motivational", "Youth speaker", "Panels & breakouts", "Challenge growth sessions"],
  },
  {
    id: "community",
    icon: HeartHandshake,
    title: "Community Engagement",
    tag: "Districts · Nonprofits",
    body: "Engage stakeholders to achieve desirable outcomes — deepening relationships and trust between families, agencies, and community organizations.",
    points: ["Collective impact", "Strategic partnerships", "Community events", "Athletic training programs"],
  },
];

const EXPERIENCE_FIELD = "Which experience are you interested in?";

function ServiceDetail({ s, onInquire, testId }) {
  return (
    <div data-testid={testId}>
      <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{s.body}</p>
      <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {s.points.map((p) => (
          <li key={p} className="flex items-start gap-2.5 text-sm text-foreground/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {p}
          </li>
        ))}
      </ul>
      <Button
        data-testid={`${testId}-inquire`}
        onClick={() => onInquire(s)}
        className="mt-7 rounded-full bg-gold px-6 font-semibold text-ink transition-all duration-300 hover:bg-gold-hover"
      >
        <Send className="mr-2 h-4 w-4" /> Request Info
      </Button>
    </div>
  );
}

export default function Services() {
  const [active, setActive] = useState(SERVICES[0]);
  const [openMobile, setOpenMobile] = useState(null);
  const [inquiryForm, setInquiryForm] = useState(null);
  const [inquiryFor, setInquiryFor] = useState(null); // service title string

  useEffect(() => {
    api.get("/forms?purpose=experience_inquiry")
      .then(({ data }) => setInquiryForm(data[0] || null))
      .catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="services-section" className="relative border-y border-gold/10 bg-ink-card/40 py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14"
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Transformational Services</span>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
            Choose your Hunter experience.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Every engagement starts with a conversation — tap an experience to explore it, then request info and we'll reach out.
          </p>
        </motion.div>

        {/* Mobile: accordion */}
        <div className="space-y-3 lg:hidden" data-testid="services-accordion">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isOpen = openMobile === s.id;
            return (
              <div key={s.id} data-testid={`service-accordion-${s.id}`}
                className={`overflow-hidden rounded-2xl border transition-colors duration-300 ${isOpen ? "border-gold/40 bg-ink-card" : "border-gold/15 bg-ink"}`}>
                <button
                  data-testid={`service-accordion-toggle-${s.id}`}
                  onClick={() => setOpenMobile(isOpen ? null : s.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                >
                  <span className="flex items-center gap-4">
                    <Icon className={`h-5 w-5 transition-colors ${isOpen ? "text-gold" : "text-muted-foreground"}`} />
                    <span>
                      <span className="block font-display text-base font-semibold">{s.title}</span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.tag}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2.5">
                    <span className="font-mono text-xs text-muted-foreground/50">0{i + 1}</span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-6">
                        <ServiceDetail s={s} onInquire={(svc) => setInquiryFor(svc.title)} testId={`service-detail-${s.id}`} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Desktop: unchanged tabs + panel */}
        <div className="hidden grid-cols-1 gap-8 lg:grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="flex flex-col gap-2" role="tablist" aria-label="Services">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const selected = active.id === s.id;
              return (
                <motion.button
                  key={s.id}
                  data-testid={`service-tab-${s.id}`}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActive(s)}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
                  className={`group flex items-center justify-between rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
                    selected
                      ? "border-gold/60 bg-gold/10 shadow-[0_0_30px_rgba(212,175,55,0.12)]"
                      : "border-gold/10 bg-ink/60 hover:border-gold/30 hover:bg-ink-card"
                  }`}
                >
                  <span className="flex items-center gap-4">
                    <Icon className={`h-5 w-5 transition-colors ${selected ? "text-gold" : "text-muted-foreground group-hover:text-gold/70"}`} />
                    <span>
                      <span className={`block font-display text-lg font-semibold ${selected ? "text-foreground" : "text-foreground/80"}`}>{s.title}</span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.tag}</span>
                    </span>
                  </span>
                  <span className={`font-mono text-xs ${selected ? "text-gold" : "text-muted-foreground/40"}`}>0{i + 1}</span>
                </motion.button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              data-testid="service-detail-panel"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="relative overflow-hidden rounded-2xl border border-gold/20 bg-ink p-8 sm:p-12"
            >
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{active.tag}</span>
              <h3 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">{active.title}</h3>
              <ServiceDetail s={active} onInquire={(svc) => setInquiryFor(svc.title)} testId="service-detail-desktop" />
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Contact AB Hunter for pricing
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <Dialog open={inquiryFor !== null} onOpenChange={(o) => !o && setInquiryFor(null)}>
        <DialogContent data-testid="experience-inquiry-dialog" className="max-h-[90vh] overflow-y-auto border-gold/25 bg-ink-card text-foreground sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Request info{inquiryFor ? ` — ${inquiryFor}` : ""}</DialogTitle>
          </DialogHeader>
          {inquiryForm ? (
            <DynamicForm
              key={inquiryFor || "general"}
              form={inquiryForm}
              initialData={inquiryFor ? { [EXPERIENCE_FIELD]: inquiryFor } : {}}
              testIdPrefix="experience-inquiry"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-gold" />
              <p className="text-sm text-muted-foreground">Loading the form…</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
